import React, { useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Grow,
} from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import {
  setConfirmationModalState,
  deleteAssumptionGroup,
  copyAssumptionGroup
} from 'store/actions';
import { parseISO, format } from 'date-fns';
import Chip from '@material-ui/core/Chip';

const GroupCard = ({
  classes,
  onClick,
  group,
  module,
  setConfirmationModalState,
  deleteAssumptionGroup,
  copyAssumptionGroup,
}) => {
  // == STATE ===================================================

  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);

  // == EVENT HANDLERS ==========================================

  const openMoreMenu = (e) => {
    setMoreMenuAnchorEl(e.currentTarget);
  };

  const closeMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const handleGroupCardClick = () => {
    onClick(group.groupId);
  };

  const handleDeleteGroup = (e) => {
    closeMoreMenu();
    setConfirmationModalState({
      isModalOpen: true,
      handleConfirmation: () => {
        closeConfirmationModal();
        deleteAssumptionGroup(group.groupId);
      },
      handleCancellation: () => {
        closeConfirmationModal();
      },
      titleText: 'Confirm',
      contentText: `Are you sure you want to delete ${group.name}?`,
    });
  };

  const handleCopyClicked = () => {
    closeMoreMenu();
    var payload = {
      groupId: group.groupId,
      selectedModuleId: module.moduleId
    }

    copyAssumptionGroup(payload);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  };

  // == Component =========================================================

  return (
    <Grow in={true}>
      <div className={classes.listItemContainer}>
        <div className={classes.cardContainer}>
          <ListItem
            button
            className={classes.listItem}
            onClick={handleGroupCardClick}
          >
            <ListItemAvatar className={classes.listItemAvatar}>
              <Avatar
                className={classNames(classes.queueAvatar, classes.blueAvatar)}
              >
                <HomeIcon />
                <Typography className={classes.avatarText} />
              </Avatar>
            </ListItemAvatar>

            <div className={classes.listItemContent}>
              <Grid
                container
                wrap='nowrap'
                spacing={0}
                alignItems='flex-start'
                direction='column'
                justifyContent='space-between'
              >
                <Grid item className={classes.groupSummaryContainer}>
                  <Typography className={classes.textPrimary} component='span'>
                    {group.name}
                  </Typography>
                </Grid>
                <Grid item className={classes.groupSummaryContainer}>
                  <Typography className={classes.textSecondary} component='div'>
                    {group.description}
                  </Typography>
                </Grid>
                <Grid item className={classes.groupSummaryContainer}>
                  <Typography
                    className={classNames(
                      classes.textSecondary,
                      classes.groupSummaryContent
                    )}
                    component='span'
                  >
                    <strong>Type</strong> - {group.assumptionGroupType}
                  </Typography>
                  {group.numFields > 0 && (
                    <Typography
                      className={classNames(
                        classes.textSecondary,
                        classes.groupSummaryContent
                      )}
                      component='span'
                    >
                      | <strong>Fields</strong> - {group.numFields}
                    </Typography>
                  )}
                  {group.numShapes > 0 && (
                    <Typography
                      className={classNames(
                        classes.textSecondary,
                        classes.groupSummaryContent
                      )}
                      component='span'
                    >
                      | <strong>Shapes</strong> - {group.numShapes}
                    </Typography>
                  )}
                </Grid>
                <Grid item className={classes.groupSummaryContainer}>
                  {group.isActive && (
                    <Chip
                      classes={{
                        root: classes.compTypeChip,
                        label: classes.label
                      }}
                      style={{
                        backgroundColor: '#67c12e',
                        width: 60,
                        marginRight: 4
                      }}
                      label='Active'
                    />
                  )}
                  {group.publishedOn && (
                    <Typography
                      className={classes.textSecondary}
                      component='span'
                    >
                      Published on {format(parseISO(group.publishedOn), 'MM/dd/yyyy, h:mm a')}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </div>
          </ListItem>
          <IconButton className={classes.detailButton} onClick={openMoreMenu}>
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={moreMenuAnchorEl}
            open={Boolean(moreMenuAnchorEl)}
            onClose={closeMoreMenu}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom',
            }}
          >
            {!group?.wasPublished &&
              <MenuItem onClick={handleDeleteGroup}>Delete group</MenuItem>
            }
            <MenuItem onClick={handleCopyClicked}>Copy group</MenuItem>
          </Menu>
        </div>
      </div>
    </Grow>
  );
};

// == Styles ===========================================================

const styles = (theme) => ({
  listItemContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '2px 8px',
  },
  cardContainer: {
    display: 'flex',
    borderRadius: 4,
    borderBottom: '1px solid #e4e4e4',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  textPrimary: {
    fontSize: '.85rem',
    fontWeight: '500',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: 8,
  },
  textSecondary: {
    fontSize: '.75rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    margin: '-3px 0px 0px',
  },
  listItem: {
    padding: 0,
    backgroundColor: '#fff',
    minWidth: 0,
  },
  listItemContent: {
    padding: '0 8px',
    width: '100%',
    minHeight: 66,
    display: 'flex',
    flexDirection: 'column',
  },
  listItemSelected: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)!important',
  },
  queueAvatar: {
    height: '72px',
    width: '24px',
    borderRadius: 0,
  },
  avatar: {
    marginRight: '0px',
    height: 68,
    width: 24,
    borderRadius: 0,
  },
  avatarText: {
    fontSize: '1.55rem',
    fontWeight: '500',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  groupSummaryContainer: {
    overflow: 'hidden',
    display: 'flex',
    marginBottom: 2,
    alignItems: 'baseline'
  },
  groupSummaryContent: {
    marginRight: '4px',
  },
  detailButton: {
    padding: 4,
    position: 'absolute',
    right: 0,
    top: 20,
  },
  listItemAvatar: {
    minWidth: 'unset',
  },
  blueAvatar: {
    backgroundColor: theme.palette.primary.main,
  },
  compTypeChip: {
    height: 18,
    color: '#fff'
  },
  label: {
    overflow: 'initial'
  }
});

// == Connect =======================================================

const mapStateToProps = () => {
  return {}
};

const mapDispatchToProps = {
  setConfirmationModalState,
  deleteAssumptionGroup,
  copyAssumptionGroup,
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(GroupCard);
